/**
 * iter166 — Bloc "Cockpit admin" affiché au-dessus du Dashboard pour les admin.
 * 4 tuiles stratégiques :
 *   - CA du mois calendaire courant
 *   - Factures fournisseurs à payer
 *   - Factures clients en retard
 *   - OR en cours (ventilation par statut)
 *
 * Chargement au montage seulement. Endpoint GET /api/dashboard/admin-cockpit.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp, FileWarning, AlertTriangle, Wrench,
  ChevronRight, ArrowRight,
} from "lucide-react";
import api from "@/lib/api";

const OR_STATUT_LABELS = {
  cree: "Créés",
  a_faire: "À faire",
  commencement: "En cours",
  controle: "Contrôle",
  travaux_effectue: "Terminés",
  pret: "Prêts",
};

const OR_STATUT_STYLES = {
  cree: "bg-gray-50 text-gray-700 border-gray-200",
  a_faire: "bg-blue-50 text-blue-700 border-blue-200",
  commencement: "bg-yellow-50 text-yellow-700 border-yellow-200",
  controle: "bg-purple-50 text-purple-700 border-purple-200",
  travaux_effectue: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pret: "bg-[#52a675]/10 text-[#3d8a5c] border-[#52a675]/30",
};

function KpiTile({ label, value, sublabel, icon: Icon, accent, testId, onClick }) {
  const Wrap = onClick ? "button" : "div";
  return (
    <Wrap
      onClick={onClick}
      data-testid={testId}
      className={`text-left w-full block ${onClick ? "cursor-pointer" : ""}`}
    >
      <Card className={`p-5 border-gray-200 h-full ${onClick ? "hover:shadow-md hover:border-[#52a675]/40 transition-all" : ""}`}>
        <div className="flex items-start justify-between mb-3">
          <div className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">
            {label}
          </div>
          <div className={`w-9 h-9 rounded-md ${accent} flex items-center justify-center flex-shrink-0`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
        <div className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 leading-tight">
          {value}
        </div>
        {sublabel && (
          <div className="text-xs text-gray-500 mt-1.5">{sublabel}</div>
        )}
      </Card>
    </Wrap>
  );
}

const fmtChf = (n) =>
  new Intl.NumberFormat("fr-CH", { style: "currency", currency: "CHF", maximumFractionDigits: 0 }).format(n || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-CH", { day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
};

export default function AdminCockpit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/dashboard/admin-cockpit");
        setData(d);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const ca = data?.ca_mois;
  const ff = data?.factures_a_payer;
  const rt = data?.factures_clients_en_retard;
  const or = data?.or_en_cours;

  const nowMonth = new Date().toLocaleDateString("fr-CH", { month: "long", year: "numeric" });

  return (
    <div className="mb-8" data-testid="admin-cockpit">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-heading font-semibold text-gray-900 text-lg">
          Cockpit — vue direction
        </h2>
        <span className="text-xs text-gray-400 uppercase tracking-wider">Admin</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* CA du mois — HT (convention comptable suisse : le CA est HT, la TVA est un pass-through pour l'AFC) */}
        <KpiTile
          label={`CA du mois — ${nowMonth}`}
          value={loading ? "—" : fmtChf(ca?.montant_ht)}
          sublabel={loading
            ? "Chargement…"
            : `${ca?.count || 0} facture${(ca?.count || 0) > 1 ? "s" : ""} · ${fmtChf(ca?.montant_ttc)} TTC (TVA ${fmtChf(ca?.montant_tva)})`
          }
          icon={TrendingUp}
          accent="bg-emerald-50 text-emerald-600"
          testId="cockpit-tile-ca-mois"
          onClick={() => navigate("/facturation")}
        />

        {/* Factures fournisseurs à payer */}
        <KpiTile
          label="Factures à payer"
          value={loading ? "—" : (ff?.count || 0)}
          sublabel={loading ? "Chargement…" : `${fmtChf(ff?.montant_ttc)} à payer aux fournisseurs`}
          icon={FileWarning}
          accent="bg-orange-50 text-orange-600"
          testId="cockpit-tile-ff-a-payer"
          onClick={() => navigate("/factures-fournisseurs?statut=a_payer")}
        />

        {/* Factures clients en retard */}
        <KpiTile
          label="Clients en retard"
          value={loading ? "—" : (rt?.count || 0)}
          sublabel={loading ? "Chargement…" : `${fmtChf(rt?.montant_ttc)} · factures échues non payées`}
          icon={AlertTriangle}
          accent="bg-red-50 text-red-600"
          testId="cockpit-tile-clients-retard"
          onClick={() => navigate("/facturation?statut=en_retard")}
        />

        {/* OR en cours */}
        <KpiTile
          label="OR en cours"
          value={loading ? "—" : (or?.total || 0)}
          sublabel={loading ? "Chargement…" : "Tous statuts actifs confondus"}
          icon={Wrench}
          accent="bg-blue-50 text-blue-600"
          testId="cockpit-tile-or-en-cours"
          onClick={() => navigate("/atelier")}
        />
      </div>

      {/* Détails : top 5 FF + Top 5 factures en retard + ventilation OR par statut */}
      {!loading && data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          {/* Top 5 FF à payer */}
          <Card className="border-gray-200 overflow-hidden" data-testid="cockpit-ff-top5">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <FileWarning className="w-4 h-4 text-orange-600" />
                Prochaines échéances fournisseurs
              </div>
              <button
                onClick={() => navigate("/factures-fournisseurs?statut=a_payer")}
                className="text-xs text-[#52a675] hover:underline flex items-center gap-1"
              >
                Voir tout <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            <ul className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
              {(ff?.top5 || []).length === 0 && (
                <li className="p-4 text-center text-xs text-gray-400">Rien à payer 🎉</li>
              )}
              {(ff?.top5 || []).map((f) => (
                <li key={f.id}>
                  <button
                    onClick={() => navigate(`/factures-fournisseurs/${f.id}`)}
                    className="w-full text-left p-3 hover:bg-gray-50 flex items-center gap-3"
                    data-testid={`cockpit-ff-item-${f.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">
                        {f.fournisseur_nom || f.reference_interne || "Sans fournisseur"}
                      </div>
                      <div className="text-[11px] text-gray-500">
                        {f.reference_interne} · échéance {fmtDate(f.date_echeance)}
                      </div>
                    </div>
                    <div className="font-mono font-bold text-sm text-gray-800">
                      {f.montant_ttc ? fmtChf(f.montant_ttc) : "—"}
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                </li>
              ))}
            </ul>
          </Card>

          {/* Top 5 clients en retard */}
          <Card className="border-gray-200 overflow-hidden" data-testid="cockpit-retard-top5">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                Factures clients en retard
              </div>
              <button
                onClick={() => navigate("/facturation?statut=en_retard")}
                className="text-xs text-[#52a675] hover:underline flex items-center gap-1"
              >
                Voir tout <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            <ul className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
              {(rt?.top5 || []).length === 0 && (
                <li className="p-4 text-center text-xs text-gray-400">Aucun retard client 👌</li>
              )}
              {(rt?.top5 || []).map((f) => (
                <li key={f.id}>
                  <button
                    onClick={() => navigate(`/facturation/${f.id}`)}
                    className="w-full text-left p-3 hover:bg-gray-50 flex items-center gap-3"
                    data-testid={`cockpit-retard-item-${f.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">
                        {f.contact_label || "—"}
                      </div>
                      <div className="text-[11px] text-gray-500">
                        {f.numero} · échéance {fmtDate(f.date_echeance)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-bold text-sm text-gray-800">
                        {fmtChf(f.total_ttc)}
                      </div>
                      {f.jours_retard != null && (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px] mt-1">
                          {f.jours_retard} j
                        </Badge>
                      )}
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                </li>
              ))}
            </ul>
          </Card>

          {/* Ventilation OR par statut */}
          <Card className="border-gray-200 overflow-hidden" data-testid="cockpit-or-par-statut">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Wrench className="w-4 h-4 text-blue-600" />
                OR par statut
              </div>
              <button
                onClick={() => navigate("/atelier")}
                className="text-xs text-[#52a675] hover:underline flex items-center gap-1"
              >
                Ouvrir <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            <div className="p-4 space-y-2">
              {Object.entries(or?.par_statut || {}).map(([statut, count]) => (
                <div key={statut} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">{OR_STATUT_LABELS[statut] || statut}</span>
                  <Badge
                    variant="outline"
                    className={`${OR_STATUT_STYLES[statut] || "bg-gray-50 text-gray-700"} min-w-[2.5rem] justify-center`}
                    data-testid={`cockpit-or-badge-${statut}`}
                  >
                    {count}
                  </Badge>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
